/**
 * UsageAlertBanner — quiet line showing how many free uses of an action are left today.
 * Becomes a highlighted alert only when running low or used up.
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, LinearProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useGate } from '../../hooks/useGate';
import { USAGE_ALERT_TEXTS, ACTION_LABELS, getGuestUsage } from '../../constants/gateConfig';

const UsageAlertBanner = ({ actionCode, language = 'en' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { peekUsage, isAuthenticated } = useGate();
  const usage = peekUsage(actionCode);
  const txt = USAGE_ALERT_TEXTS[language] || USAGE_ALERT_TEXTS.en;
  const actionLabel = (ACTION_LABELS[language] || ACTION_LABELS.en)[actionCode] || actionCode;

  // Nothing to show for unlimited (paid) users or blocked features (the gate modal explains those)
  if (usage.isUnlimited || usage.limit === 0) return null;

  const { used, limit, remaining } = usage;
  const isExhausted = remaining <= 0;
  const isLow = !isExhausted && remaining <= 1 && used > 0;

  const resetHours = () => {
    const { resetAt } = getGuestUsage();
    if (!resetAt) return 24;
    return Math.max(1, Math.ceil((24 * 60 * 60 * 1000 - (Date.now() - resetAt)) / (60 * 60 * 1000)));
  };

  let message;
  if (isExhausted) {
    message = isAuthenticated
      ? txt.limitReached.replace('{action}', actionLabel).replace('{hours}', resetHours())
      : txt.limitReachedGuest.replace('{action}', actionLabel);
  } else {
    message = txt.remaining.replace('{remaining}', remaining).replace('{limit}', limit).replace('{action}', actionLabel);
  }

  const tone = isExhausted ? 'error' : isLow ? 'warning' : null;

  return (
    <Box
      sx={(theme) => ({
        mb: 2,
        px: tone ? 1.5 : 0.25,
        py: tone ? 1.25 : 0,
        borderRadius: '12px',
        border: tone ? '1px solid' : 'none',
        borderColor: tone ? alpha(theme.palette[tone].main, 0.4) : 'transparent',
        bgcolor: tone ? alpha(theme.palette[tone].main, theme.palette.mode === 'dark' ? 0.12 : 0.06) : 'transparent',
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: tone ? `${tone}.main` : 'text.secondary', fontWeight: 600 }}>
            {message}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (used / limit) * 100)}
            color={tone || 'primary'}
            sx={{ mt: 0.5, height: 4, maxWidth: tone ? 'none' : 240 }}
            aria-label={message}
          />
        </Box>
        {tone && (
          <Button
            size="small"
            variant="outlined"
            color={tone}
            onClick={() => navigate(isAuthenticated ? '/app/account#passes' : `/auth?mode=signup&next=${encodeURIComponent(location.pathname)}`)}
            sx={{ flexShrink: 0 }}
          >
            {isAuthenticated ? txt.upgrade : txt.signUp}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default UsageAlertBanner;
